import { useState, useEffect, useCallback } from 'react';
import type { MetricsSummary, StateMetrics, OrderFact, OrderDetail, MetricsRange } from '../api';
import OrdersTable from './OrdersTable';
import OrderDetailsModal from './OrderDetailsModal';

// Data fetcher interface for API abstraction
export interface DashboardDataFetcher {
  getMetrics: (range: MetricsRange) => Promise<MetricsSummary>;
  getMetricsByState: (range: MetricsRange) => Promise<{ states: StateMetrics[]; range: string }>;
  getOrders: (params: { range?: MetricsRange; limit?: number; cursor?: number }) => Promise<{ orders: OrderFact[]; nextCursor: number | null }>;
  getOrderDetail: (shopifyOrderId: string) => Promise<{ order: OrderDetail }>;
}

interface DashboardViewProps {
  dataFetcher: DashboardDataFetcher;
  title: string;
  subtitle?: string;
  readOnly?: boolean;
  showBackLink?: boolean;
  onBack?: () => void;
  hideKpis?: boolean;
}

function formatCurrency(amount: number | string, currency = 'USD') {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(num);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Range selector component
function RangeSelector({
  value,
  onChange,
}: {
  value: MetricsRange;
  onChange: (range: MetricsRange) => void;
}) {
  const ranges: { value: MetricsRange; label: string }[] = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: 'all', label: 'All Time' },
  ];

  return (
    <div className="flex gap-1 bg-ink-900/50 p-1 rounded-lg">
      {ranges.map((r) => (
        <button
          key={r.value}
          onClick={() => onChange(r.value)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            value === r.value
              ? 'bg-ink-700 text-ink-50 shadow-sm'
              : 'text-ink-400 hover:text-ink-200'
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

// State bar chart component
function StateChart({ states }: { states: StateMetrics[] }) {
  if (states.length === 0) {
    return <p className="text-ink-500 text-center py-8">No data available</p>;
  }

  const maxOrders = Math.max(...states.map((s) => s.orderCount));

  return (
    <div className="space-y-3">
      {states.slice(0, 10).map((state, idx) => (
        <div key={state.province} className="flex items-center gap-3">
          <div className="w-12 text-right">
            <span className="text-sm font-mono text-ink-300">{state.province}</span>
          </div>
          <div className="flex-1">
            <div className="h-6 bg-ink-800/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent-500 to-royal-500 rounded-full transition-all duration-500"
                style={{
                  width: `${(state.orderCount / maxOrders) * 100}%`,
                  animationDelay: `${idx * 50}ms`,
                }}
              />
            </div>
          </div>
          <div className="w-20 text-right">
            <span className="text-sm font-medium text-ink-200">{state.orderCount}</span>
            <span className="text-xs text-ink-500 ml-1">orders</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DashboardView({
  dataFetcher,
  title,
  subtitle,
  readOnly = false,
  showBackLink = false,
  onBack,
  hideKpis = false,
}: DashboardViewProps) {
  const [range, setRange] = useState<MetricsRange>('30d');
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [states, setStates] = useState<StateMetrics[]>([]);
  const [orders, setOrders] = useState<OrderFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState<OrderFact | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [metricsData, statesData, ordersData] = await Promise.all([
        dataFetcher.getMetrics(range),
        dataFetcher.getMetricsByState(range),
        dataFetcher.getOrders({ range, limit: 50 }),
      ]);

      setMetrics(metricsData);
      setStates(statesData.states);
      setOrders(ordersData.orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [dataFetcher, range]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle order click - fetch detail and open modal
  const handleOrderClick = async (order: OrderFact) => {
    setSelectedOrder(order);
    setModalLoading(true);
    setModalError(null);
    setOrderDetail(null);

    try {
      const { order: detail } = await dataFetcher.getOrderDetail(order.shopify_order_id);
      setOrderDetail(detail);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to load order details');
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setOrderDetail(null);
    setModalError(null);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      {!hideKpis && <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {showBackLink && onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-ink-400 hover:text-ink-200 text-sm mb-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Influencers
            </button>
          )}
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-display font-bold text-ink-50">{title}</h1>
            {readOnly && (
              <span className="px-2 py-1 text-xs font-medium bg-royal-500/20 text-royal-400 rounded-full border border-royal-500/30">
                View Only
              </span>
            )}
          </div>
          {subtitle && <p className="text-ink-400 mt-1">{subtitle}</p>}
        </div>
        <RangeSelector value={range} onChange={setRange} />
      </div>}
      {hideKpis && <div className="flex justify-end"><RangeSelector value={range} onChange={setRange} /></div>}

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      {!hideKpis && (loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stat-card animate-pulse">
              <div className="h-4 w-24 bg-ink-700 rounded mb-2" />
              <div className="h-8 w-32 bg-ink-700 rounded" />
            </div>
          ))}
        </div>
      ) : metrics ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stat-card animate-slide-up stagger-1">
            <p className="text-ink-400 text-sm mb-1">Total Orders</p>
            <p className="text-3xl font-display font-bold text-ink-50">
              {metrics.totalOrders.toLocaleString()}
            </p>
          </div>
          <div className="stat-card animate-slide-up stagger-2">
            <p className="text-ink-400 text-sm mb-1">Total Revenue</p>
            <p className="text-3xl font-display font-bold text-accent-400">
              {formatCurrency(metrics.totalRevenue, metrics.currency)}
            </p>
          </div>
          <div className="stat-card animate-slide-up stagger-3">
            <p className="text-ink-400 text-sm mb-1">Avg. Order Value</p>
            <p className="text-3xl font-display font-bold text-ink-50">
              {formatCurrency(metrics.averageOrderValue, metrics.currency)}
            </p>
          </div>
          <div className="stat-card animate-slide-up stagger-4">
            <p className="text-ink-400 text-sm mb-1">Currency</p>
            <p className="text-3xl font-display font-bold text-royal-400">
              {metrics.currency}
            </p>
          </div>
        </div>
      ) : null)}

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Orders by State */}
        <div className="card p-6 animate-slide-up stagger-3">
          <h2 className="text-lg font-semibold text-ink-50 mb-4">Orders by State</h2>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-6 bg-ink-800 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <StateChart states={states} />
          )}
        </div>

        {/* Recent Orders Preview */}
        <div className="card overflow-hidden animate-slide-up stagger-4">
          <div className="p-4 border-b border-ink-800/50">
            <h2 className="text-lg font-semibold text-ink-50">Recent Orders</h2>
          </div>

          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-ink-800 rounded animate-pulse" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-ink-500">No orders yet</div>
          ) : (
            <div className="divide-y divide-ink-800/30">
              {orders.slice(0, 5).map((order) => (
                <div
                  key={order.id}
                  onClick={() => handleOrderClick(order)}
                  className="p-4 flex items-center justify-between hover:bg-ink-800/20 transition-colors cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOrderClick(order);
                    }
                  }}
                >
                  <div>
                    <p className="font-mono text-sm text-ink-200">
                      #{order.shopify_order_id}
                    </p>
                    <p className="text-xs text-ink-500 mt-0.5">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-ink-100">
                      {formatCurrency(order.total_price, order.currency)}
                    </p>
                    {(order.order_state || order.shipping_province) && (
                      <p className="text-xs text-ink-500">{order.order_state || order.shipping_province}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full Orders Table */}
      <div className="table-container animate-slide-up stagger-5">
        <div className="table-header p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink-50">All Orders</h2>
          <p className="text-sm text-ink-500">Click any row to view details</p>
        </div>
        <OrdersTable
          orders={orders}
          isLoading={loading}
          onOrderClick={handleOrderClick}
        />
      </div>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={orderDetail}
        isOpen={!!selectedOrder}
        onClose={closeModal}
        isLoading={modalLoading}
        error={modalError}
      />
    </div>
  );
}
